import { createElement, useCallback } from "react";
import { useDeleteCampaign } from "../../App/Queries/CampaignQueries";
import { useNotification } from "../../App/State/Hooks/notificationHooks";
import { useDebouncedHandler } from "../../App/State/Hooks/useDebouncedHandler";
import { ICampaign } from "../../App/Typings/Campaigns";
import DeleteCampaignInner from "../../ManageCampaigns/Components/Dialog/DeleteCampaignInner";
import { PartialDialog } from "../../ManageCampaigns/State/DialogState";

export default function useDeleteCampaignDialogState(
  handleDisplayDialog: (dialog: PartialDialog) => void,
  handleClearDialog: () => void,
  onCampaignDeleted?: () => void,
) {
  const { displayError, displaySuccess } = useNotification();
  const { mutateAsync: deleteCampaign, isLoading } = useDeleteCampaign();

  const handleSubmitDeleteCampaign = useDebouncedHandler(
    useCallback(
      async (campaignId: string, shouldArchiveGroup: boolean) => {
        if (isLoading) {
          return;
        }
        if (!campaignId || campaignId === "") {
          displayError("Error deleting form");
          return;
        }
        await deleteCampaign(
          { campaignId, shouldArchiveGroup },
          {
            onError: () => {
              displayError("Error deleting form");
            },
            onSuccess: () => {
              displaySuccess("Form deleted");
              handleClearDialog();
              onCampaignDeleted?.();
            },
          },
        );
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [isLoading],
    ),
    300,
  );

  const handleOpenDeleteDialog = useCallback(
    (campaign: ICampaign) => {
      handleDisplayDialog({
        "aria-label": "Delete Form",
        maxWidth: "sm",
        children: createElement(DeleteCampaignInner, {
          campaignId: campaign.id,
          campaignName: campaign.name,
          handleSubmitDeleteCampaign,
          handleClearDialog,
        }),
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isLoading],
  );

  return {
    handleSubmitDeleteCampaign,
    handleOpenDeleteDialog,
  } as const;
}
